import Select, { createFilter } from "react-select";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import { TIMEZONE_OPTIONS } from "utils/timeZones";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, error, light } = colors;
const { borderWidth } = borders;

const customStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    height: "40px",
    background: rgba(black.main, 0.025),
    border: state.isFocused
      ? `${borderWidth[1]} solid ${rgba(primary.main, 0.65)}`
      : `${borderWidth[1]} solid ${rgba(black.main, 0.18)}`,
    borderRadius: "16px",
    boxShadow: state.isFocused ? `0 0 0 3px ${rgba(primary.main, 0.12)}` : "none",
    backdropFilter: "none",
    paddingLeft: "8px",
    transition: "all 0.2s ease",
    cursor: "text",
    fontSize: "0.75rem",

    "&:hover": {
      border: `${borderWidth[1]} solid ${rgba(primary.main, 0.45)}`,
    },
  }),

  valueContainer: (base) => ({
    ...base,
    height: "40px",
    padding: "0 14px",
  }),

  input: (base) => ({
    ...base,
    color: dark.main,
    margin: 0,
    padding: 0,
  }),

  placeholder: (base) => ({
    ...base,
    color: rgba(dark.main, 0.55),
    fontSize: "0.75rem",
  }),

  singleValue: (base) => ({
    ...base,
    color: dark.main,
    fontSize: "0.75rem",
    fontWeight: 500,
  }),

  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: rgba(black.main, 0.16),
    marginTop: 12,
    marginBottom: 12,
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? primary.main : rgba(dark.main, 0.75),

    "&:hover": {
      color: primary.main,
    },
  }),

  clearIndicator: (base) => ({
    ...base,
    color: rgba(dark.main, 0.65),

    "&:hover": {
      color: error.main,
    },
  }),

  menu: (base) => ({
    ...base,
    background: light?.main || "#e6ded1",
    border: `${borderWidth[1]} solid ${rgba(black.main, 0.14)}`,
    borderRadius: "18px",
    overflow: "hidden",
    boxShadow: `0 14px 34px ${rgba(black.main, 0.18)}`,
    zIndex: 9999,
  }),

  menuList: (base) => ({
    ...base,
    padding: "8px",
  }),

  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? rgba(primary.main, 0.14)
      : state.isFocused
        ? rgba(primary.main, 0.08)
        : "transparent",
    color: dark.main,
    borderRadius: "12px",
    padding: "12px 16px",
    fontSize: "0.75rem",
    fontWeight: state.isSelected ? 700 : 500,
    cursor: "pointer",

    "&:active": {
      background: rgba(primary.main, 0.14),
    },
  }),

  noOptionsMessage: (base) => ({
    ...base,
    color: rgba(dark.main, 0.65),
    fontSize: "0.75rem",
  }),

  loadingMessage: (base) => ({
    ...base,
    color: rgba(dark.main, 0.65),
    fontSize: "0.75rem",
  }),
};

const TimezoneSelectField = ({ value, onChange, error: fieldError, touched }) => {
  const selectedOption = TIMEZONE_OPTIONS.find((tz) => tz.value === value) || null;

  return (
    <VuiBox>
      <VuiTypography variant="caption" color="dark" fontWeight="medium">
        Time Zone *
      </VuiTypography>

      <Select
        isSearchable
        options={TIMEZONE_OPTIONS}
        value={selectedOption}
        onChange={(option) => onChange(option?.value)}
        styles={customStyles}
        placeholder="Select Timezone"
        filterOption={createFilter({
          ignoreCase: true,
          ignoreAccents: true,
          trim: true,
          matchFrom: "any",
        })}
      />

      {touched && fieldError && (
        <VuiTypography variant="caption" color="error" fontWeight="medium">
          {fieldError}
        </VuiTypography>
      )}
    </VuiBox>
  );
};

export default TimezoneSelectField;
